import { getListingCrops, type ListingCrop } from './listingCropsRun';
import { getSubmodels } from './listingStore';
import { getParts } from './partsRun';
import { viewAxes, type BoxAxes } from './viewDirections';

// HOW BIG AN UNMEASURED PART IS, READ OFF THE PHOTOGRAPHS. Arithmetic, and no call.
//
// Everything it needs was already answered by earlier phases, and the last piece arrived with
// the mm-per-pixel table: a photograph classified front, back, left or right has a KNOWN scale
// — the mean of what the parts that ARE measured imply on it — and a part's box on that
// photograph is a number of pixels. Pixels times millimetres-per-pixel is the part's size, on
// the two axes that picture can see.
//
// WHICH IS WHY THIS IS NOT A MODEL'S QUESTION. Asked of one, "how many times does this go
// into the seat" is the same proportion computed by eye, out of a reading nobody can check,
// once per press and at a price. Here every number is the box, the scale and the multiplication
// — and where it is wrong it is wrong for a reason you can point at: that box is loose, or that
// picture's ruler was one part.
//
// PER PICTURE, and never averaged into one answer. A part's width off the front view and off
// the back view are two readings of one thing, and their disagreement is the interesting half:
// it is the perspective, the boxes, and the classification, all in one column of numbers. A
// single consolidated millimetre would hide exactly that.

// One picture's reading of one part: which photograph, which side of the product it shows, and
// the two dimensions that side can measure. `across` is width on a front or back view and
// depth on a side one; `up` is the height either way — see viewAxes.
export type ScaleEstimate = {
  url: string;
  view: string;
  axes: BoxAxes;
  across: number | null;
  up: number | null;
  // What the reading was made of, so it can be argued with: the picture's scale per axis, the
  // part's box in that picture's own pixels, and the measured parts the scale came from.
  // The part's rectangle on that photograph, normalized — the very box the pixels were
  // counted from. Kept so the picture can be shown with it drawn: a reading of "398 mm across"
  // is a claim about one rectangle, and the photograph without it is a chair.
  bbox: [number, number, number, number];
  scaleAcross: number | null;
  scaleUp: number | null;
  wide: number;
  tall: number;
  rulers: string[];
};

export type Pixels = Map<string, { w: number; h: number }>;

// WHICH SIDE EACH PHOTOGRAPH SHOWS, by URL, across every submodel — Phase 1's answer. A
// direction is a fact about the picture and not about the colourway it belongs to, and the
// first submodel to name a URL keeps it.
export function viewByUrl(docId: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const sub of getSubmodels(docId)) {
    sub.gallery.forEach((url, i) => {
      const view = sub.galleryViews[i];
      if (view && !out.has(url)) out.set(url, view);
    });
  }
  return out;
}

// THE BOX TO MEASURE A PART BY, on one photograph: the biggest one that is not behind the rest
// of the object.
//
// Biggest because a part found twice is the near instance and the far one, and the near one is
// the least foreshortened — the same rule the zoom's labels follow. `far` is dropped outright:
// its pixels are at another distance, so the picture's scale does not apply to them. And the
// model's own rectangle where there is one, not the padded copy: the padding is our arithmetic,
// and measuring it would return the part plus a margin of ours.
const boxOn = (crops: ListingCrop[], url: string): [number, number, number, number] | null => {
  let best: [number, number, number, number] | null = null;
  let area = 0;
  for (const crop of crops) {
    if (crop.url !== url || crop.off || crop.depth === 'far') continue;
    const box = crop.rawBbox ?? crop.bbox;
    const size = Math.max(0, box[2] - box[0]) * Math.max(0, box[3] - box[1]);
    if (size > area) {
      area = size;
      best = box;
    }
  }
  return best;
};

// WHAT A PIXEL OF ONE PHOTOGRAPH IS WORTH, per axis: the mean of every measured part's own
// answer to that question on it.
//
// A mean rather than a pick, because each measured part is a reading of the same picture and
// none of them is the authority. Where two disagree by a percent or two it is their boxes being
// a few pixels loose; where they disagree by a fifth, something is wrong, and the table under
// the zoomed photograph is where that shows.
//
// Both axes go through the picture's real pixels first. One unit of x is its whole WIDTH and
// one of y its whole HEIGHT, so a normalized box compares the two axes an aspect ratio apart
// until that conversion has happened.
// WHAT ONE PHOTOGRAPH'S SCALE IS: the mean of what the parts on it that are already MEASURED
// imply, in mm per pixel along each of its two axes.
//
// `skip` is the part being estimated, and leaving it out is not a nicety. A part keeps its
// answer, so the second press found it in this list holding the size the FIRST press gave it —
// and a ruler that says "this box is 398 mm" produces a scale that says this box is 398 mm.
// The estimate reproduced itself exactly, every press, for ever: pressing the button on a part
// already estimated could not change its number, which is the one thing pressing it is for.
//
// AND A DERIVED SIZE IS NOT A RULER EITHER. The account this writes opens "what the parts that
// ARE measured imply", and it was true of the words only: any size counted, so one estimate
// became the ruler for the next and the error compounded down the chain with nothing on screen
// admitting it. A ruler is a number read off the retailer's dimension drawing.
function scaleOf(
  docId: string, url: string, axes: BoxAxes, pixels: Pixels, skip: number,
): { across: number | null; up: number | null; rulers: string[] } {
  const px = pixels.get(url);
  if (!px) return { across: null, up: null, rulers: [] };
  const parts = getParts(docId);
  const byPart = getListingCrops(docId);
  const across: number[] = [];
  const up: number[] = [];
  const rulers: string[] = [];
  for (const [key, crops] of Object.entries(byPart)) {
    if (Number(key) === skip) continue;
    const part = parts[Number(key)];
    const size = part?.size;
    if (!size || size.source !== 'measured') continue;
    const box = boxOn(crops ?? [], url);
    if (!box) continue;
    const wide = Math.max(0, box[2] - box[0]) * px.w;
    const tall = Math.max(0, box[3] - box[1]) * px.h;
    const mmAcross = size[axes.across];
    const mmUp = size[axes.up];
    const had = across.length + up.length;
    if (typeof mmAcross === 'number' && mmAcross > 0 && wide > 1) across.push(mmAcross / wide);
    if (typeof mmUp === 'number' && mmUp > 0 && tall > 1) up.push(mmUp / tall);
    // Named where it actually contributed: the derivation has to say what the ruler WAS, and
    // a part visible on the picture that gave neither axis a number is not one.
    if (across.length + up.length > had) rulers.push(part?.nameEn || part?.name || '?');
  }
  const mean = (got: number[]) => (got.length ? got.reduce((a, b) => a + b, 0) / got.length : null);
  return { across: mean(across), up: mean(up), rulers };
}

// WHETHER THIS PART IS THE QUESTION AT ALL: no width, no depth, no height.
//
// Not "missing one of them" — a part with two axes and a blank third was measured, and the
// blank is what its drawing did not dimension. This is for the parts nothing has sized, which
// is most of a chair and the whole reason the scale is worth computing.
export const noMeasurements = (size: { x?: number | null; y?: number | null; z?: number | null } | null | undefined) =>
  size?.x == null && size?.y == null && size?.z == null;

// ONE PART, READ OFF EVERY PHOTOGRAPH THAT CAN MEASURE IT.
//
// Which photographs those are: square-on views — front, back, left, right — where this part
// has a box that is not `far`. A 45° shot is excluded because both horizontal extents project
// into the box's width there, so what the bottom edge spans is neither the width nor the depth.
//
// A picture whose scale could not be read is left out rather than reported empty: it means no
// measured part is visible on it, which is a fact about the ruler and not about this part.
export function scaleEstimates(docId: string, at: number, pixels: Pixels): ScaleEstimate[] {
  const crops = getListingCrops(docId)[at] ?? [];
  if (!crops.length) return [];
  const views = viewByUrl(docId);
  const out: ScaleEstimate[] = [];
  const seen = new Set<string>();
  for (const crop of crops) {
    if (seen.has(crop.url)) continue;
    seen.add(crop.url);
    const view = views.get(crop.url);
    const axes = viewAxes(view);
    if (!view || !axes) continue;
    const box = boxOn(crops, crop.url);
    const px = pixels.get(crop.url);
    if (!box || !px) continue;
    const scale = scaleOf(docId, crop.url, axes, pixels, at);
    if (scale.across == null && scale.up == null) continue;
    const wide = Math.max(0, box[2] - box[0]) * px.w;
    const tall = Math.max(0, box[3] - box[1]) * px.h;
    out.push({
      url: crop.url,
      view,
      bbox: box,
      axes,
      across: scale.across == null ? null : Math.round(scale.across * wide),
      up: scale.up == null ? null : Math.round(scale.up * tall),
      scaleAcross: scale.across,
      scaleUp: scale.up,
      wide: Math.round(wide),
      tall: Math.round(tall),
      rulers: scale.rulers,
    });
  }
  return out;
}

// THE PART'S SIZE, out of every picture that could measure it: one number per axis, the mean of
// its readings.
//
// A mean, because two pictures reading one dimension are two readings and not two answers — the
// front view and the back view of the same seat differ by the perspective between them and by
// how tight each box was. Rounded once, at the end.
//
// `x`, `y` and `z` and nothing else: this is a size read off a photograph, and a photograph of a
// chair says nothing about a thread or a drive.
export type ScaledSize = { x: number | null; y: number | null; z: number | null };

const meanOf = (got: number[]): number | null =>
  (got.length ? Math.round(got.reduce((a, b) => a + b, 0) / got.length) : null);

// THE ONE PHOTOGRAPH TO BELIEVE: the one holding the biggest box of the part.
//
// The same rule boxOn already applies WITHIN a picture — the biggest instance is the nearest to
// the camera and the least foreshortened — raised to the choice BETWEEN pictures, where it was
// not being made at all. Every readable photograph contributed and the axes were averaged, so a
// side rail seen end-on through the gap under a seat, thirty pixels of it, pulled the answer of
// a picture that had the whole rail across it.
//
// AND IT COSTS AN AXIS, ON PURPOSE. The axes a picture answers come from the direction it was
// taken from: a left view gives depth and height, a back view width and height. Keeping one
// picture keeps the two axes IT saw and leaves the third empty. That is the right trade here —
// these sizes exist to scale a mesh that already has the shape, and one dimension read off a
// good box does that better than three of which two are guesses.
export function bestRead(read: ScaleEstimate[]): ScaleEstimate | null {
  let best: ScaleEstimate | null = null;
  for (const r of read) {
    if (!best || r.wide * r.tall > best.wide * best.tall) best = r;
  }
  return best;
}

export function scaledSize(read: ScaleEstimate[]): ScaledSize {
  const by: Record<'x' | 'y' | 'z', number[]> = { x: [], y: [], z: [] };
  const r = bestRead(read);
  if (r) {
    if (r.across != null) by[r.axes.across].push(r.across);
    if (r.up != null) by[r.axes.up].push(r.up);
  }
  return { x: meanOf(by.x), y: meanOf(by.y), z: meanOf(by.z) };
}

// WHAT WAS DONE, IN WORDS — the badge's second half, and the whole of what makes an estimate
// arguable.
//
// A model's estimate is a sentence you can only believe or not. This one is a multiplication,
// so its account can name every input: the picture, the scale it was read at, which measured
// parts gave that scale, and the box in pixels. A number that looks wrong is then traceable to
// the one of those four that is.
export function scaleDerivation(read: ScaleEstimate[], dims: ScaledSize): string {
  // ONE PICTURE, WRITTEN AS THE SUM IT IS. This was three lines of shorthand — "scale 1.241
  // mm/px y", "box 321 x 62 px -> y 398 mm" — which is every number involved and none of the
  // arithmetic between them: a reader had to work out for themselves that the scale came from
  // another part, that "y" was the horizontal edge of this photograph, and that 398 was 321
  // times 1.241. Each of those is a step where the answer can be wrong, and each is now
  // written down, so disagreeing with it is a matter of reading rather than of deducing.
  const kept = bestRead(read);
  const per = read.map((r) => {
    // WHICH ONE THE ANSWER CAME FROM, said on every picture rather than only on the winner: a
    // paragraph of arithmetic that ends in a number the size never took is a paragraph nobody
    // should have to work out the standing of.
    const lines = [`${r.view.toUpperCase()} view${r === kept
      ? ' — the biggest box of this part, and the one the size below is read from'
      : ' — a smaller box of this part, worked out here and NOT used'}`];
    const from = r.rulers.length
      ? `known size of the ${r.rulers.join(' and the ')} in this photograph`
      : 'nothing';
    if (r.scaleAcross != null) {
      lines.push(`  across the picture: ${r.scaleAcross.toFixed(3)} mm per pixel`
        + ` (this edge is the part's ${r.axes.across}), from the ${from}`);
    }
    if (r.scaleUp != null) {
      lines.push(`  up the picture: ${r.scaleUp.toFixed(3)} mm per pixel`
        + ` (this edge is the part's ${r.axes.up}), from the ${from}`);
    }
    lines.push(`  this part's box here: ${r.wide} px across by ${r.tall} px up`);
    if (r.across != null) {
      lines.push(`  so ${r.axes.across} = ${r.wide} px × ${r.scaleAcross?.toFixed(3)} mm/px`
        + ` = ${r.across} mm`);
    }
    if (r.up != null) {
      lines.push(`  so ${r.axes.up} = ${r.tall} px × ${r.scaleUp?.toFixed(3)} mm/px`
        + ` = ${r.up} mm`);
    }
    return lines.join('\n');
  });
  const counted = (['x', 'y', 'z'] as const).map((k) => {
    const got = kept
      ? [kept.axes.across === k ? kept.across : null, kept.axes.up === k ? kept.up : null]
        .filter((v): v is number => v != null)
      : [];
    if (!got.length) return null;
    // THE FINAL NUMBER, and where several pictures answered the same axis, that it is their
    // mean rather than one of them. "z: 5 mm (mean of 5, 5)" said the same thing in a shape
    // that reads as a coincidence.
    return `${k} = ${dims[k]} mm`;
  }).filter(Boolean);
  return [
    'No model was asked. Each square-on photograph has a scale of its own — what the parts '
    + 'that ARE measured imply on it — and this part\'s box, in that picture\'s pixels, times '
    + 'that scale is its size. Pictures where the part sits behind the rest of the object are '
    + 'left out: their pixels are at another distance.',
    '',
    // A BLANK LINE BETWEEN THE PICTURES. Each is three lines — the scale, what set it, the box
    // and what it gives — and run together the block reads as one twelve-line paragraph in
    // which the second picture's first line is the first picture's fourth.
    per.join('\n\n'),
    '',
    ...counted,
  ].join('\n');
}

// THE SAME ACCOUNT BEFORE THE PRESS: what it would look at, and at what scale. The badge's
// first half — where a call that asks a model shows its prompt, this shows its arithmetic.
// THE SAME NUMBERS, AT THE PRECISION ANYBODY READS THEM AT. The raw panel printed the
// scale as JavaScript hands it over — "scaleAcross": 1.2171717171717171 — which is sixteen
// digits of a quotient of two measurements good to about three. The derivation above already
// says 1.217; this is the same value written the same way.
//
// Rounded for the PRINTING and not in the estimate itself: `scaledSize` multiplies these by a
// box in pixels, and rounding the input to a size would be throwing away precision the
// arithmetic still has a use for.
const round3 = (n: number | null) => (n == null ? null : Math.round(n * 1000) / 1000);

// WHOSE SIZE THIS IS, AND WHAT IT CAME TO, BEFORE THE WORKING. The raw panel opened on
// `read` — two screens of scales, boxes and rulers — and put `dims` at the bottom, past the
// fold, with nothing anywhere naming the part it was about. Which is the wrong way round for
// a panel somebody opens to check an answer: the answer first, then how it was reached.
//
// The part is named because this file's other output names the RULERS by name, and a reader
// comparing the two had no way to tell which of the parts on screen was the one being
// measured and which were measuring it.
export function scaleJson(
  part: string, read: ScaleEstimate[], dims: unknown,
): string {
  return JSON.stringify({
    part,
    dims,
    read: read.map((r) => ({
      ...r, scaleAcross: round3(r.scaleAcross), scaleUp: round3(r.scaleUp),
    })),
  }, null, 1);
}

export function scaleInputs(read: ScaleEstimate[]): string {
  if (!read.length) {
    return 'Nothing to measure this part against yet. It needs a box on a photograph '
      + 'classified front, back, left or right, and at least one MEASURED part visible on that '
      + 'same photograph to give it a scale.';
  }
  return [
    'This estimate is arithmetic, not a call. For each photograph below: the scale is the mean '
    + 'of what the measured parts on it imply, and this part\'s size is its box in pixels times '
    + 'that scale.',
    '',
    ...read.map((r) => [
      `${r.view}: box ${r.wide} x ${r.tall} px`,
      `  scale from ${r.rulers.join(', ') || 'nothing'}`,
      `  answers ${[r.axes.across, r.axes.up].join(' and ')}`,
    ].join('\n')),
  ].join('\n');
}
